<script setup lang="ts">
/**
 * Two panes. Left: the brand, the product itself — a live-looking pipeline strip on a raised
 * surface, not a stock illustration — and the quick links. Right: the form alone, centred.
 * Under 768px the product pane folds away, so the form pane carries the brand and the links.
 */
import Icon from '~/components/ui/Icon.vue'
import StatusCell from '~/components/ui/StatusCell.vue'
import ProgressCell from '~/components/ui/ProgressCell.vue'
/**
 * `showcase` draws the product pane. A refusal turns it off: the panel says who is signed in,
 * and behind it stood the signed-out display with its sample pipeline and TODO label — a screen
 * telling a customer both that they are in and that they are being sold to (W8-UI-10). Off, the
 * layout is the one it already uses under 768px: the brand above the panel, the links below it.
 */
const props = withDefaults(defineProps<{ showcase?: boolean }>(), { showcase: true })
const readError = useState<string | null>('studio-read-error', () => null)
</script>

<template>
  <div class="grid min-h-screen bg-background text-foreground" :class="props.showcase ? 'md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]' : ''">
    <aside v-if="props.showcase" class="relative hidden overflow-hidden border-r border-border bg-card px-10 py-6 md:flex md:flex-col md:items-center">
      <div class="pointer-events-none absolute -right-40 -top-40 size-[var(--measure-520px)] rounded-full bg-primary/[0.06] blur-3xl" />
      <div class="pointer-events-none absolute -bottom-48 -left-24 size-[var(--measure-420px)] rounded-full bg-info/[0.05] blur-3xl" />
      <NuxtLink to="/" class="relative flex items-center gap-2 no-underline hover:no-underline">
        <span class="grid size-5 place-items-center rounded-[var(--radius-control)] bg-gradient-primary text-[length:var(--type-meta)] font-bold text-primary-foreground shadow-glow-primary">S</span>
        <span class="text-[length:var(--type-control)] font-bold tracking-[-0.01em] text-foreground">Scalty</span>
        <span class="num rounded-[var(--radius-label)] border border-border px-1 py-0.5 text-[length:var(--type-badge)] text-muted-foreground">AI STUDIO</span>
      </NuxtLink>
      <div class="relative flex w-full max-w-[var(--measure-520px)] flex-1 flex-col justify-center gap-5 py-10">
        <p class="m-0 text-[length:var(--type-auth-title)] font-semibold leading-[1.2] tracking-[-0.01em] text-pretty">
          Every stage of a model's life, visible while it happens.
        </p>
        <div class="rounded-lg border border-border bg-background/60">
          <div class="flex h-[var(--card-header-height)] items-center gap-2.5 border-b border-border px-3">
            <span class="text-xs font-semibold">Customer Support</span><TodoBadge />
            <span class="num text-[length:var(--type-meta)] text-muted-foreground">iteration 7 · stage 3 of 4</span>
          </div>
          <div class="grid grid-cols-2 gap-2.5 p-2.5">
            <div class="flex flex-col gap-1.5 rounded-lg border-gradient-primary p-2.5">
              <span class="num flex items-center gap-1.5 text-[length:var(--type-fine)] font-semibold text-primary"><Icon name="datasets" :size="12" />01 Datasets</span>
              <span class="text-[length:var(--type-control)] font-semibold">Support tickets 2024</span>
              <StatusCell status="Ready" />
              <span class="num text-[length:var(--type-fine)] text-muted-foreground">24 files · markdown and text</span>
            </div>
            <div class="flex flex-col gap-1.5 rounded-lg border-gradient-primary p-2.5">
              <span class="num flex items-center gap-1.5 text-[length:var(--type-fine)] font-semibold text-primary"><Icon name="memory" :size="12" />02 Memory</span>
              <span class="text-[length:var(--type-control)] font-semibold">Support tickets 2024</span>
              <StatusCell status="Ready" />
              <span class="num text-[length:var(--type-fine)] text-muted-foreground">24 documents · quotable</span>
            </div>
            <div class="flex flex-col gap-1.5 rounded-lg border-gradient-accent p-2.5 shadow-[0_0_24px_-6px_hsl(var(--primary)/0.45)]">
              <span class="num flex items-center gap-1.5 text-[length:var(--type-fine)] font-semibold text-info"><Icon name="models" :size="12" />03 Train</span>
              <span class="text-[length:var(--type-control)] font-semibold">support v7</span>
              <ProgressCell :percent="62" status="Training" :bar-width="80" />
              <span class="num text-[length:var(--type-fine)] text-muted-foreground">epoch 2 of 3 · loss 0.84 · 14:32</span>
            </div>
            <div class="flex flex-col gap-1.5 rounded-lg border border-border p-2.5 opacity-85">
              <span class="num flex items-center gap-1.5 text-[length:var(--type-fine)] font-semibold text-muted-foreground"><Icon name="deploy" :size="12" />04 Deploy</span>
              <span class="text-[length:var(--type-control)] font-semibold text-muted-foreground">api-prod</span>
              <StatusCell status="Live" />
              <span class="num text-[length:var(--type-fine)] text-muted-foreground">serves v6 · 312 requests / minute</span>
            </div>
          </div>
        </div>
        <div class="num flex flex-wrap gap-5 text-[length:var(--type-meta)] text-muted-foreground">
          <span class="flex items-center gap-1.5"><span class="size-1.5 rounded-full bg-primary shadow-glow-primary" />normal</span>
          <span class="flex items-center gap-1.5"><span class="size-1.5 rounded-full bg-info shadow-glow-info" />progress</span>
          <span class="flex items-center gap-1.5"><span class="size-1.5 rounded-full bg-warning shadow-glow-warning" />waiting</span>
          <span class="flex items-center gap-1.5"><span class="size-1.5 rounded-full bg-destructive shadow-glow-destructive" />refusal</span>
        </div>
      </div>
      <div class="num relative flex flex-wrap justify-center gap-x-4 text-[length:var(--type-meta)] text-muted-foreground">
        <span>Scalty AI Studio</span>
        <a href="https://docs.scalty.tech" class="hover:text-foreground">Docs</a>
        <a href="https://status.scalty.tech" class="hover:text-foreground">Status</a>
      </div>
    </aside>

    <div class="flex flex-col items-center px-6 py-6 sm:px-10">
      <NuxtLink to="/" class="flex items-center gap-2 no-underline hover:no-underline" :class="props.showcase ? 'md:hidden' : ''">
        <span class="grid size-5 place-items-center rounded-[var(--radius-control)] bg-gradient-primary text-[length:var(--type-meta)] font-bold text-primary-foreground shadow-glow-primary">S</span>
        <span class="text-[length:var(--type-control)] font-bold tracking-[-0.01em] text-foreground">Scalty</span>
        <span class="num rounded-[var(--radius-label)] border border-border px-1 py-0.5 text-[length:var(--type-badge)] text-muted-foreground">AI STUDIO</span>
      </NuxtLink>
      <div class="flex w-full flex-1 items-center justify-center py-10">
        <div class="w-full max-w-[var(--measure-400px)]">
          <p v-if="readError" role="alert" class="mb-3 text-xs text-destructive">{{ readError }}</p>
          <slot />
        </div>
      </div>
      <div class="num flex flex-wrap justify-center gap-x-4 text-[length:var(--type-meta)] text-muted-foreground" :class="props.showcase ? 'md:hidden' : ''">
        <span>Scalty AI Studio</span>
        <a href="https://docs.scalty.tech" class="hover:text-foreground">Docs</a>
        <a href="https://status.scalty.tech" class="hover:text-foreground">Status</a>
      </div>
    </div>
  </div>
</template>
